import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import logo from '../assets/logo/light.png';
import '../styles/Login.css';

const API_BASE = process.env.REACT_APP_API_BASE;

const COUNTRY_CODES = [
  { label: '+597 Suriname',    cc: '597' },
  { label: '+31 Netherlands',  cc: '31'  },
  { label: '+592 Guyana',      cc: '592' },
];

export default function Login() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const { openModal } = useModal();

  const [cc,          setCC]          = useState('597');
  const [phone,       setPhone]       = useState('');
  const [password,    setPassword]    = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [loading,     setLoading]     = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();

    const phoneRaw = phone.replace(/\D/g, '');
    if (!phoneRaw || !password) {
      openModal({ title: 'Missing fields', text: 'Please fill in your phone number and password.' });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/sign_in`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form: {
            phone: {
              cc:        { cc },
              phone_raw: phoneRaw,
            },
            password,
          },
        }),
      });

      const data = await res.json();

      if (data.success && data.data?.account?.token) {
        login(data.data.account.token, data.data.account);
        navigate('/dashboard');
      } else {
        openModal({ title: 'Sign in failed', text: data.response || 'Something went wrong. Please try again.' });
      }
    } catch {
      openModal({ title: 'Connection error', text: 'Could not reach the server. Check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-root">

      {/* ── Left panel (sidebar-style) ── */}
      <div className="login-panel">
        <div className="login-panel-inner">

          <Link to="/" className="login-brand" style={{ textDecoration: 'none' }}>
            <img src={logo} alt="Ride Eats" className="login-brand-img" />
            <div>
              <div className="login-brand-name">Ride Eats</div>
              <div className="login-brand-sub">Developer Portal</div>
            </div>
          </Link>

          <div className="login-panel-body">
            <h2 className="login-panel-title">Keys, webhooks &amp; event logs for your POS integration.</h2>
            <ul className="login-panel-list">
              <li>Issue and rotate API keys per merchant location</li>
              <li>Register webhook endpoints for order events</li>
              <li>Inspect live delivery logs in real time</li>
            </ul>
          </div>

          <div className="login-panel-footer">
            <Link to="/docs" className="login-panel-docs-link">API Reference →</Link>
            <Link to="/" className="login-panel-home-link">← Back to home</Link>
          </div>

        </div>
      </div>

      {/* ── Right: form ── */}
      <div className="login-form-col">
        <div className="login-card">
          <h1 className="login-title">Sign in</h1>
          <p className="login-subtitle">Access is restricted to authorised merchant accounts.</p>

          <form className="login-form" onSubmit={handleSubmit} noValidate>

            <div className="login-field">
              <label className="login-label">Phone number</label>
              <div className="login-phone-row">
                <select
                  className="login-select"
                  value={cc}
                  onChange={e => setCC(e.target.value)}
                  disabled={loading}
                >
                  {COUNTRY_CODES.map(c => (
                    <option key={c.cc} value={c.cc}>{c.label}</option>
                  ))}
                </select>
                <input
                  className="login-input"
                  type="tel"
                  placeholder="7654321"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  disabled={loading}
                  autoComplete="tel"
                />
              </div>
            </div>

            <div className="login-field">
              <label className="login-label">Password</label>
              <div className="login-pass-row">
                <input
                  className="login-input"
                  type={showPass ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-pass-toggle"
                  onClick={() => setShowPass(v => !v)}
                  tabIndex={-1}
                >
                  {showPass ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? <span className="login-spinner" /> : 'Sign in'}
            </button>

          </form>
        </div>
      </div>

    </div>
  );
}
